import { useQuery } from "@tanstack/react-query";
import { Link, Outlet, useNavigate } from "@tanstack/react-router";
import { AppIcon, Button, IconCommand, IconMenu, TextField } from "@wilco/ui";
import { useCallback, useEffect, useState, type SubmitEvent } from "react";
import { meQuery } from "../auth.ts";
import { t } from "../i18n.ts";
import { BottomBar } from "./BottomBar.tsx";
import { MenuSheet } from "./MenuSheet.tsx";
import { Palette } from "./Palette.tsx";
import { SectionNav } from "./SectionNav.tsx";
import { useScanner } from "./useScanner.ts";
import { UserMenu } from "./UserMenu.tsx";
import { usePageTitle } from "./usePageTitle.ts";

/**
 * Оболочка: чёрная шапка, разделы слева на ПК, шторкой и нижней панелью
 * на телефоне (до 768 px, туда же планшеты 7–8 дюймов).
 */
export function Shell() {
  const { data: me } = useQuery(meQuery);
  const navigate = useNavigate();
  const title = usePageTitle();
  const [menuOpen, setMenuOpen] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);

  const openScan = useCallback(
    (code: string) => void navigate({ to: "/scan/$code", params: { code } }),
    [navigate],
  );

  // Сканер-клавиатура вне полей ввода: код открывает свой экран.
  useScanner(openScan);

  // Ctrl+K и ⌘K: палитра команд.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setPaletteOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  useEffect(() => {
    document.title = t("app.title", { page: title });
  }, [title]);

  return (
    <div className="shell">
      <header className="shell-header">
        <Button
          variant="header"
          size="icon"
          className="shell-compact-only"
          aria-label={t("shell.menu")}
          aria-expanded={menuOpen}
          onClick={() => setMenuOpen(true)}
        >
          <IconMenu />
        </Button>
        <Link to="/" className="shell-brand" aria-label={t("section.desk")}>
          <AppIcon name="overview" size={28} />
          <span className="shell-brand-name">{t("app.brand")}</span>
        </Link>
        {/* Где я (docs/ui-rules.md, «9»): на телефоне в шапке название экрана. */}
        <span className="shell-title" aria-live="polite">
          {title}
        </span>
        {me?.testLogin ? (
          // Вход через поддельный Google стенда (ops/dev-stand, --mock-oidc):
          // видно на каждом экране и скриншоте, что данные и учётки тестовые.
          <span className="shell-test-login" role="note">
            {t("shell.testLogin")}
          </span>
        ) : null}
        <HeaderScan onScan={openScan} />
        <Button
          variant="header"
          size="icon"
          className="shell-palette-btn"
          aria-label={`${t("shell.palette")} (${t("shell.palette.hint")})`}
          onClick={() => setPaletteOpen(true)}
        >
          <IconCommand />
        </Button>
        {me ? <UserMenu me={me} /> : null}
      </header>

      <aside className="shell-side">
        <SectionNav />
      </aside>

      <main className="shell-main" id="main">
        <Outlet />
      </main>

      <BottomBar onScan={openScan} />
      <MenuSheet open={menuOpen} onOpenChange={setMenuOpen} />
      <Palette open={paletteOpen} onOpenChange={setPaletteOpen} onScan={openScan} />
    </div>
  );
}

/** Поле скана в шапке, только на ПК: на телефоне скан в нижней панели. */
function HeaderScan({ onScan }: { onScan: (code: string) => void }) {
  const [code, setCode] = useState("");

  const submit = (e: SubmitEvent) => {
    e.preventDefault();
    const value = code.trim();
    // Поле сразу пустое: следующий скан не склеится с прошлым.
    setCode("");
    if (value) onScan(value);
  };

  return (
    <form className="shell-scan" role="search" onSubmit={submit}>
      <TextField
        label={t("scan.label")}
        hideLabel
        variant="scan"
        placeholder={t("scan.placeholder")}
        value={code}
        onChange={(e) => setCode(e.target.value)}
        autoComplete="off"
        spellCheck={false}
        enterKeyHint="search"
        data-scan-field=""
      />
    </form>
  );
}
